import { useEffect, useMemo, useState } from "react";
import {
  connectParasutWithCode,
  getParasutAuthorizationUrl,
  getParasutConnectionStatus,
  listParasutContacts,
  syncParasutContacts
} from "../services/parasutService";
import { sendBalanceReminderSms, sendNetgsmTestSms } from "../services/netgsmService";

function ParasutIntegrationView({ themeMode = "light", firmaListesi = [] }) {
  const BALANCE_REMINDER_THRESHOLD = 3000;
  const isDark = themeMode === "dark";
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearchText, setContactSearchText] = useState("");
  const [authorizationUrl, setAuthorizationUrl] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [busyMessage, setBusyMessage] = useState("");
  const [resultMessage, setResultMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [messageContext, setMessageContext] = useState("");
  const [testSmsPhone, setTestSmsPhone] = useState("");
  const [testSmsMessage, setTestSmsMessage] = useState("e-cAlp Netgsm test SMS'i başarıyla gönderildi.");
  const [activeSection, setActiveSection] = useState("firms");
  const [selectedReminderIds, setSelectedReminderIds] = useState([]);
  const [smsReminderTemplate, setSmsReminderTemplate] = useState("Sayın {firma}, güncel bakiyeniz {bakiye} olarak görünmektedir. Ödeme yapıldıysa bu mesajı dikkate almayınız.");
  const [whatsappReminderTemplate, setWhatsappReminderTemplate] = useState("Sayın {firma}, güncel bakiyeniz {bakiye} olarak görünmektedir. Ödeme yapıldıysa lütfen bu mesajı dikkate almayınız. e-cAlp Yazılım Danışmanlık.");
  const [mailSubjectTemplate, setMailSubjectTemplate] = useState("Güncel bakiye bilgilendirmesi - {firma}");
  const [mailBodyTemplate, setMailBodyTemplate] = useState("Merhaba,\n\n{firma} için güncel bakiye {bakiye} olarak görünmektedir.\n\nÖdeme yapıldıysa bu bilgilendirmeyi dikkate almayınız.\n\nİyi çalışmalar.");

  const colors = useMemo(() => ({
    pageText: isDark ? "#e5edf6" : "#124254",
    muted: isDark ? "#9fb0c4" : "#64748b",
    surface: isDark ? "#162133" : "#ffffff",
    soft: isDark ? "#1e293b" : "#f8fafc",
    border: isDark ? "#334155" : "#dbe2ea",
    input: isDark ? "#101827" : "#ffffff",
    inputText: isDark ? "#e5edf6" : "#0f172a"
  }), [isDark]);

  const inputStyle = {
    width: "100%",
    padding: "12px 13px",
    borderRadius: 11,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.inputText,
    boxSizing: "border-box",
    fontWeight: 700
  };

  const cardStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: 20,
    boxShadow: isDark ? "0 12px 30px rgba(0,0,0,0.18)" : "0 8px 24px rgba(15,52,66,0.07)"
  };

  const normalizedFirmOptions = useMemo(() => {
    const map = new Map();
    (firmaListesi || []).forEach((firma) => {
      const name = String(firma?.firmaAdi || firma?.name || firma || "").trim();
      const shortName = String(firma?.firmaKisaAdi || firma?.shortName || "").trim();
      const key = normalizeMatchText(name);

      if (!key || map.has(key)) return;

      map.set(key, {
        name,
        shortName,
        nameKey: key,
        shortKey: normalizeMatchText(shortName)
      });
    });
    return Array.from(map.values());
  }, [firmaListesi]);

  const enrichedContacts = useMemo(() => {
    return contacts.map((contact) => ({
      ...contact,
      portalMatch: findPortalMatch(contact, normalizedFirmOptions)
    }));
  }, [contacts, normalizedFirmOptions]);

  const filteredContacts = useMemo(() => {
    const search = normalizeMatchText(contactSearchText);
    if (!search) return enrichedContacts;

    return enrichedContacts.filter((contact) => {
      return [
        contact.name,
        contact.taxNumber,
        contact.email,
        contact.phone,
        contact.category,
        contact.portalMatch?.name
      ].some((value) => normalizeMatchText(value).includes(search));
    });
  }, [contactSearchText, enrichedContacts]);

  const filteredPendingBalanceTotal = useMemo(() => {
    return filteredContacts.reduce((total, contact) => {
      const balance = Number(contact.balance || 0);
      return balance > 0 ? total + balance : total;
    }, 0);
  }, [filteredContacts]);

  const contactStats = useMemo(() => {
    return {
      total: contacts.length,
      matched: enrichedContacts.filter((contact) => contact.portalMatch).length,
      withTaxNumber: contacts.filter((contact) => contact.taxNumber).length,
      withBalance: contacts.filter((contact) => contact.balance !== null && contact.balance !== "" && contact.balance !== undefined).length
    };
  }, [contacts, enrichedContacts]);

  const positiveBalanceContacts = useMemo(() => {
    return enrichedContacts.filter((contact) => Number(contact.balance || 0) > 0);
  }, [enrichedContacts]);

  const defaultReminderContacts = useMemo(() => {
    return positiveBalanceContacts.filter((contact) =>
      Number(contact.balance || 0) > BALANCE_REMINDER_THRESHOLD &&
      (normalizePhoneForDisplay(contact.phone) || String(contact.email || "").trim())
    );
  }, [positiveBalanceContacts]);

  const selectedReminderContacts = useMemo(() => {
    const selectedSet = new Set(selectedReminderIds);
    return positiveBalanceContacts.filter((contact) => selectedSet.has(contact.id));
  }, [positiveBalanceContacts, selectedReminderIds]);

  const reminderStats = useMemo(() => {
    return {
      positive: positiveBalanceContacts.length,
      overThreshold: defaultReminderContacts.length,
      withPhone: positiveBalanceContacts.filter((contact) => normalizePhoneForDisplay(contact.phone)).length,
      withEmail: positiveBalanceContacts.filter((contact) => String(contact.email || "").trim()).length,
      selected: selectedReminderContacts.length
    };
  }, [defaultReminderContacts, positiveBalanceContacts, selectedReminderContacts]);

  const primaryButtonStyle = {
    padding: "11px 15px",
    borderRadius: 11,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer"
  };

  const secondaryButtonStyle = {
    padding: "11px 15px",
    borderRadius: 11,
    border: `1px solid ${colors.border}`,
    background: colors.soft,
    color: colors.pageText,
    fontWeight: 900,
    cursor: "pointer"
  };
  const getBalanceBadgeStyle = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return {
        background: isDark ? "#1e293b" : "#f1f5f9",
        color: isDark ? "#cbd5e1" : "#475569",
        border: isDark ? "1px solid #334155" : "1px solid #e2e8f0"
      };
    }

    if (numberValue < 0) {
      return {
        background: isDark ? "rgba(127,29,29,0.35)" : "#fee2e2",
        color: isDark ? "#fecaca" : "#991b1b",
        border: isDark ? "1px solid rgba(248,113,113,0.25)" : "1px solid #fecaca"
      };
    }

    if (numberValue === 0) {
      return {
        background: isDark ? "rgba(71,85,105,0.35)" : "#f8fafc",
        color: isDark ? "#cbd5e1" : "#64748b",
        border: isDark ? "1px solid #334155" : "1px solid #e2e8f0"
      };
    }

    return {
      background: isDark ? "rgba(22,101,52,0.28)" : "#dcfce7",
      color: isDark ? "#bbf7d0" : "#166534",
      border: isDark ? "1px solid rgba(74,222,128,0.28)" : "1px solid #bbf7d0"
    };
  };
  const sectionTabs = [
    { key: "firms", label: "Firmalar" },
    { key: "connection", label: "Paraşüt Bağlantısı" },
    { key: "reminders", label: "Gönderimler" },
    { key: "sms", label: "SMS Testi" },
    { key: "automation", label: "Otomasyon Planı" }
  ];

  const loadStatus = async () => {
    setLoadingStatus(true);
    setErrorMessage("");

    try {
      const data = await getParasutConnectionStatus();
      setStatus(data);
      setCompanyId((current) => current || data.companyId || "");
    } catch (error) {
      setErrorMessage(error.message || "Paraşüt bağlantı durumu alınamadı.");
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadContacts = async () => {
    setLoadingContacts(true);

    try {
      const data = await listParasutContacts();
      setContacts(Array.isArray(data.contacts) ? data.contacts : []);
    } catch (error) {
      setErrorMessage(error.message || "Paraşüt cari listesi alınamadı.");
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadContacts();
  }, []);

  useEffect(() => {
    setSelectedReminderIds((current) => {
      if (current.length > 0) return current;
      return defaultReminderContacts.map((contact) => contact.id);
    });
  }, [defaultReminderContacts]);

  const createAuthorizationLink = async () => {
    setMessageContext("connection");
    setBusyMessage("Yetkilendirme linki hazırlanıyor...");
    setResultMessage("");
    setErrorMessage("");

    try {
      const data = await getParasutAuthorizationUrl();
      setAuthorizationUrl(data.url || "");
      setResultMessage("Link hazır. Paraşüt ekranında izin verdikten sonra oluşan kodu buraya yapıştır.");
    } catch (error) {
      setErrorMessage(error.message || "Yetkilendirme linki alınamadı.");
    } finally {
      setBusyMessage("");
    }
  };

  const connectWithCode = async () => {
    if (!authCode.trim()) {
      setErrorMessage("Paraşüt yetki kodu zorunlu.");
      return;
    }

    setMessageContext("connection");
    setBusyMessage("Paraşüt bağlantısı kuruluyor...");
    setResultMessage("");
    setErrorMessage("");

    try {
      await connectParasutWithCode({
        code: authCode.trim(),
        companyId: companyId.trim()
      });
      setAuthCode("");
      setResultMessage("Paraşüt bağlantısı kuruldu.");
      await loadStatus();
      await loadContacts();
    } catch (error) {
      setErrorMessage(error.message || "Paraşüt bağlantısı kurulamadı.");
    } finally {
      setBusyMessage("");
    }
  };

  const syncContacts = async () => {
    setMessageContext("sync");
    setBusyMessage("Cariler Paraşüt'ten çekiliyor...");
    setResultMessage("");
    setErrorMessage("");

    try {
      const data = await syncParasutContacts({ companyId: companyId.trim() });
      setResultMessage(`${data.syncedCount || 0} cari senkronize edildi.`);
      await loadStatus();
      await loadContacts();
    } catch (error) {
      setErrorMessage(error.message || "Cari senkronizasyonu başarısız oldu.");
    } finally {
      setBusyMessage("");
    }
  };

  const sendTestSms = async () => {
    if (!testSmsPhone.trim()) {
      setErrorMessage("Test SMS için telefon numarası gir.");
      return;
    }

    setMessageContext("sms");
    setBusyMessage("Test SMS gönderiliyor...");
    setResultMessage("");
    setErrorMessage("");

    try {
      const data = await sendNetgsmTestSms({
        phone: testSmsPhone.trim(),
        message: testSmsMessage.trim()
      });
      setResultMessage(`Test SMS Netgsm'e iletildi.${data?.netgsmResponse ? ` Takip cevabı: ${data.netgsmResponse}` : ""}`);
    } catch (error) {
      setErrorMessage(error.message || "Test SMS gönderilemedi.");
    } finally {
      setBusyMessage("");
    }
  };

  const toggleReminderContact = (contactId) => {
    setSelectedReminderIds((current) => (
      current.includes(contactId)
        ? current.filter((id) => id !== contactId)
        : [...current, contactId]
    ));
  };

  const selectAllReminderContacts = () => {
    setSelectedReminderIds(
      defaultReminderContacts.map((contact) => contact.id)
    );
  };

  const clearReminderSelection = () => {
    setSelectedReminderIds([]);
  };

  const sendSelectedReminderSms = async () => {
    if (selectedReminderContacts.length === 0) {
      setErrorMessage("SMS göndermek için en az bir cari seç.");
      return;
    }

    const selectedWithPhone = selectedReminderContacts.filter((contact) => normalizePhoneForDisplay(contact.phone));
    if (selectedWithPhone.length === 0) {
      setErrorMessage("Seçili carilerde telefon numarası yok.");
      return;
    }

    const mailOnlyCount = selectedReminderContacts.filter((contact) =>
      !normalizePhoneForDisplay(contact.phone) &&
      String(contact.email || "").trim()
    ).length;
    const confirmed = window.confirm(
      `${selectedWithPhone.length} cari için bakiye SMS'i gönderilecek.` +
      (mailOnlyCount ? ` ${mailOnlyCount} cari sadece e-posta bilgisinde olduğu için SMS gönderimine dahil edilmeyecek.` : "") +
      " Onaylıyor musun?"
    );
    if (!confirmed) return;

    setBusyMessage("Bakiye SMS'leri gönderiliyor...");
    setMessageContext("reminders");
    setResultMessage("");
    setErrorMessage("");

    try {
      const data = await sendBalanceReminderSms({
        contactIds: selectedWithPhone.map((contact) => contact.id),
        messageTemplate: smsReminderTemplate
      });
      setResultMessage(`${data.sentCount || 0} SMS gönderildi. ${data.skippedCount || 0} atlandı, ${data.failedCount || 0} hata.`);
    } catch (error) {
      setErrorMessage(error.message || "Bakiye SMS'leri gönderilemedi.");
    } finally {
      setBusyMessage("");
    }
  };

  const openMailDraftForSelected = () => {
    const contactsWithEmail = selectedReminderContacts.filter((item) => String(item.email || "").trim());

    if (contactsWithEmail.length === 0) {
      setErrorMessage("Mail taslağı için e-posta adresi olan en az bir cari seç.");
      return;
    }

    if (contactsWithEmail.length === 1) {
      const contact = contactsWithEmail[0];
      const subject = renderClientTemplate(mailSubjectTemplate, contact);
      const body = renderClientTemplate(mailBodyTemplate, contact);
      window.location.href = `mailto:${encodeURIComponent(contact.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      return;
    }

    const recipients = contactsWithEmail.map((contact) => String(contact.email || "").trim()).join(";");
    const subject = "Güncel bakiye bilgilendirmesi";
    const body = contactsWithEmail.map((contact) => {
      return [
        `${contact.name || "-"}: ${formatMoney(contact.balance)}`,
        contact.taxNumber ? `Vergi No: ${contact.taxNumber}` : ""
      ].filter(Boolean).join(" - ");
    }).join("\n");

    window.location.href = `mailto:${encodeURIComponent(recipients)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openWhatsAppForContact = (contact) => {
    const whatsappPhone = normalizePhoneForWhatsApp(contact?.phone);

    if (!whatsappPhone) {
      setMessageContext("reminders");
      setErrorMessage("WhatsApp için geçerli telefon numarası bulunamadı.");
      return;
    }

    const message = renderClientTemplate(whatsappReminderTemplate, contact);
    const url = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openWhatsAppForSelected = () => {
    const contactsWithPhone = selectedReminderContacts.filter((contact) => normalizePhoneForWhatsApp(contact.phone));

    if (contactsWithPhone.length === 0) {
      setMessageContext("reminders");
      setErrorMessage("WhatsApp için telefon numarası olan en az bir cari seç.");
      return;
    }

    const confirmed = window.confirm(
      `${contactsWithPhone.length} cari için WhatsApp mesaj ekranı açılacak. Tarayıcı çoklu sekmeleri engellerse satırdaki WhatsApp butonlarını tek tek kullanabilirsin. Devam edilsin mi?`
    );

    if (!confirmed) return;

    setMessageContext("reminders");
    setResultMessage(`${contactsWithPhone.length} WhatsApp mesaj bağlantısı açılıyor.`);
    setErrorMessage("");

    contactsWithPhone.forEach((contact, index) => {
      window.setTimeout(() => openWhatsAppForContact(contact), index * 250);
    });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: colors.pageText }}>Paraşüt Cari Eşleştirme</h2>
          <div style={{ color: colors.muted, fontWeight: 800, marginTop: 6 }}>
            Paraşüt carilerini portala çekip vergi numarası/kategori ile eşleştirme altyapısı
          </div>
        </div>

        <button type="button" onClick={loadStatus} style={secondaryButtonStyle}>
          Durumu Yenile
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 16,
          padding: 8,
          borderRadius: 16,
          background: colors.surface,
          border: `1px solid ${colors.border}`
        }}
      >
        {sectionTabs.map((tab) => {
          const isActive = activeSection === tab.key;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveSection(tab.key)}
              style={{
                border: isActive ? "1px solid transparent" : `1px solid ${colors.border}`,
                background: isActive ? "linear-gradient(135deg, #124254, #0f766e)" : colors.soft,
                color: isActive ? "#fff" : colors.pageText,
                padding: "11px 15px",
                borderRadius: 999,
                fontWeight: 950,
                cursor: "pointer"
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeSection === "connection" && (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 0.9fr) minmax(320px, 1.1fr)", gap: 16 }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: colors.pageText }}>Bağlantı Durumu</h3>

          {loadingStatus ? (
            <div style={{ color: colors.muted, fontWeight: 800 }}>Durum kontrol ediliyor...</div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "inline-flex",
                  width: "fit-content",
                  padding: "7px 11px",
                  borderRadius: 999,
                  background: status?.connected ? "#dcfce7" : "#fee2e2",
                  color: status?.connected ? "#166534" : "#991b1b",
                  fontWeight: 900
                }}
              >
                {status?.connected ? "Bağlı" : "Bağlı değil"}
              </div>

              <InfoRow label="Şirket ID" value={status?.companyId || "Henüz girilmedi"} colors={colors} />
              <InfoRow label="Bağlayan" value={status?.connectedBy || "-"} colors={colors} />
              <InfoRow label="Son senkron" value={formatDate(status?.lastSyncAt) || "-"} colors={colors} />
              <InfoRow label="Son kayıt sayısı" value={String(status?.lastSyncCount || 0)} colors={colors} />
            </div>
          )}
        </div>

        <div style={cardStyle} className={isDark ? "" : "force-light-form"}>
          <h3 style={{ marginTop: 0, color: colors.pageText }}>Bağlantı Kur</h3>
          <p style={{ marginTop: 0, color: colors.muted, fontWeight: 700, lineHeight: 1.5 }}>
            Önce Paraşüt yetkilendirme linkini oluştur. Açılan ekranda izin verince Paraşüt bir kod verecek; o kodu buraya yapıştırıp bağlayacağız.
          </p>

          <div style={{ display: "grid", gap: 12 }}>
            <button type="button" onClick={createAuthorizationLink} disabled={Boolean(busyMessage)} style={primaryButtonStyle}>
              Yetkilendirme Linki Oluştur
            </button>

            {authorizationUrl && (
              <div style={{ padding: 12, borderRadius: 12, background: colors.soft, border: `1px solid ${colors.border}` }}>
                <a href={authorizationUrl} target="_blank" rel="noreferrer" style={{ color: isDark ? "#7dd3fc" : "#0369a1", fontWeight: 900 }}>
                  Paraşüt yetkilendirme ekranını aç
                </a>
              </div>
            )}

            <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
              Paraşüt Şirket ID
              <input
                value={companyId}
                onChange={(event) => setCompanyId(event.target.value)}
                placeholder="Örn: 123456"
                style={inputStyle}
              />
            </label>

            <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
              Yetki Kodu
              <textarea
                value={authCode}
                onChange={(event) => setAuthCode(event.target.value)}
                placeholder="Paraşüt ekranında verilen kodu yapıştır"
                rows={3}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </label>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={connectWithCode} disabled={Boolean(busyMessage)} style={primaryButtonStyle}>
                Paraşüt'e Bağla
              </button>

              <button type="button" onClick={syncContacts} disabled={Boolean(busyMessage) || !status?.connected} style={{ ...secondaryButtonStyle, opacity: status?.connected ? 1 : 0.55 }}>
                Carileri Senkronize Et
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {activeSection === "automation" && (
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <h3 style={{ marginTop: 0, color: colors.pageText }}>Sonraki Adım</h3>
        <div style={{ color: colors.muted, fontWeight: 750, lineHeight: 1.6 }}>
          Senkronizasyondan sonra Paraşüt carileri <strong>vergi numarası</strong>, firma adı ve kategori alanlarına göre portaldaki firmalarla eşleştirilecek.
          Ardından bakiye hatırlatma günleri, e-posta/SMS şablonları ve otomatik gönderim kurallarını bu ekrana ekleyeceğiz.
        </div>
      </div>
      )}

      {activeSection === "sms" && (
      <div style={{ ...cardStyle, marginTop: 16 }} className={isDark ? "" : "force-light-form"}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: colors.pageText }}>Netgsm SMS Testi</h3>
            <div style={{ color: colors.muted, fontWeight: 750, marginTop: 6 }}>
              Otomatik bakiye hatırlatmalarına geçmeden önce tek numaraya test SMS gönder.
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 0.6fr) minmax(260px, 1.4fr) auto", gap: 10, marginTop: 14, alignItems: "end" }}>
          <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
            Telefon
            <input
              value={testSmsPhone}
              onChange={(event) => setTestSmsPhone(event.target.value)}
              placeholder="05XXXXXXXXX"
              style={inputStyle}
            />
          </label>

          <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
            Mesaj
            <input
              value={testSmsMessage}
              onChange={(event) => setTestSmsMessage(event.target.value)}
              placeholder="Test mesajı"
              style={inputStyle}
            />
          </label>

          <button
            type="button"
            onClick={sendTestSms}
            disabled={Boolean(busyMessage)}
            style={{ ...primaryButtonStyle, minHeight: 44 }}
          >
            Test SMS Gönder
          </button>
        </div>
      </div>
      )}

      {activeSection === "reminders" && (
      <div style={{ ...cardStyle, marginTop: 16 }} className={isDark ? "" : "force-light-form"}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: colors.pageText }}>Bakiye Hatırlatma Gönderimleri</h3>
            <div style={{ color: colors.muted, fontWeight: 750, marginTop: 6, lineHeight: 1.5 }}>
              3.000 TL üzeri bakiyesi olan ve telefon veya e-posta bilgisi bulunan cariler otomatik seçili gelir.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={selectAllReminderContacts} style={secondaryButtonStyle}>3.000 TL Üzeri Bakiyeleri Seç</button>
            <button type="button" onClick={clearReminderSelection} style={secondaryButtonStyle}>Seçimi temizle</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(120px, 1fr))", gap: 8, marginTop: 14 }}>
          <StatCard label="Pozitif bakiye" value={reminderStats.positive} colors={colors} isDark={isDark} compact />
          <StatCard label="3.000 TL üzeri" value={reminderStats.overThreshold} colors={colors} isDark={isDark} compact />
          <StatCard label="WhatsApp/SMS var" value={reminderStats.withPhone} colors={colors} isDark={isDark} compact />
          <StatCard label="E-posta var" value={reminderStats.withEmail} colors={colors} isDark={isDark} compact />
          <StatCard label="Seçili cari" value={reminderStats.selected} colors={colors} isDark={isDark} compact />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 14 }}>
          <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
            SMS Şablonu
            <textarea
              value={smsReminderTemplate}
              onChange={(event) => setSmsReminderTemplate(event.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
            />
            <span style={{ color: colors.muted, fontSize: 11 }}>
              Kullanılabilir alanlar: {"{firma}"}, {"{bakiye}"}, {"{vergiNo}"}, {"{kategori}"}
            </span>
          </label>

          <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
            WhatsApp Şablonu
            <textarea
              value={whatsappReminderTemplate}
              onChange={(event) => setWhatsappReminderTemplate(event.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
            />
            <span style={{ color: colors.muted, fontSize: 11 }}>
              WhatsApp Web açılır, mesaj hazır gelir; son gönderimi kullanıcı yapar.
            </span>
          </label>

          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
              Mail Konusu
              <input
                value={mailSubjectTemplate}
                onChange={(event) => setMailSubjectTemplate(event.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 6, color: colors.pageText, fontWeight: 900 }}>
              Mail Metni
              <textarea
                value={mailBodyTemplate}
                onChange={(event) => setMailBodyTemplate(event.target.value)}
                rows={4}
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
              />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          <button type="button" onClick={sendSelectedReminderSms} disabled={Boolean(busyMessage)} style={primaryButtonStyle}>
            Seçili Carilere SMS Gönder
          </button>
          <button
            type="button"
            onClick={openWhatsAppForSelected}
            style={{
              ...primaryButtonStyle,
              background: "#16a34a"
            }}
          >
            Seçili Carileri WhatsApp'ta Aç
          </button>
          <button type="button" onClick={openMailDraftForSelected} style={secondaryButtonStyle}>
            Seçili Cariler İçin Mail Taslağı Aç
          </button>
        </div>

        <div style={{ marginTop: 14, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "42px minmax(260px, 1.7fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) minmax(150px, 0.9fr) minmax(130px, 0.8fr) minmax(112px, 0.7fr)",
              gap: 8,
              padding: "8px 10px",
              background: colors.soft,
              color: colors.muted,
              fontSize: 11,
              fontWeight: 950
            }}
          >
            <div>Seç</div>
            <div>Cari</div>
            <div style={{ textAlign: "right", paddingRight: 8 }}>
              Bakiye
              <span
                style={{
                  display: "inline-flex",
                  marginLeft: 8,
                  padding: "3px 7px",
                  borderRadius: 999,
                  background: isDark ? "#153c33" : "#dcfce7",
                  color: isDark ? "#bbf7d0" : "#166534",
                  border: isDark ? "1px solid #166534" : "1px solid #86efac",
                  fontWeight: 1000,
                  fontSize: 10,
                  whiteSpace: "nowrap"
                }}
              >
                Toplam {formatMoney(filteredPendingBalanceTotal)}
              </span>
            </div>
            <div>Telefon</div>
            <div>E-posta</div>
            <div>Durum</div>
            <div>İşlem</div>
          </div>

          {positiveBalanceContacts.length === 0 ? (
            <div style={{ padding: 14, color: colors.muted, fontWeight: 850, fontSize: 12 }}>
              Pozitif bakiyeli cari bulunamadı.
            </div>
          ) : (
            positiveBalanceContacts.map((contact) => {
              const isSelected = selectedReminderIds.includes(contact.id);
              const phone = normalizePhoneForDisplay(contact.phone);
              const hasEmail = Boolean(String(contact.email || "").trim());
              const channelLabel = [
                phone ? "SMS" : "",
                hasEmail ? "Mail" : ""
              ].filter(Boolean).join(" + ") || "İletişim eksik";

              return (
                <div
                  key={contact.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "42px minmax(260px, 1.7fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) minmax(150px, 0.9fr) minmax(130px, 0.8fr) minmax(112px, 0.7fr)",
                    gap: 8,
                    padding: "7px 10px",
                    borderTop: `1px solid ${colors.border}`,
                    color: colors.pageText,
                    fontWeight: 800,
                    alignItems: "center",
                    fontSize: 12,
                    background: isSelected ? (isDark ? "rgba(14,116,144,0.14)" : "#f0fdfa") : "transparent"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleReminderContact(contact.id)}
                  />
                  <div style={{ fontWeight: 950 }}>{contact.name || "-"}</div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ display: "inline-flex", minWidth: 96, justifyContent: "flex-end", padding: "5px 8px", borderRadius: 10, fontWeight: 1000, ...getBalanceBadgeStyle(contact.balance) }}>
                      {formatMoney(contact.balance)}
                    </span>
                  </div>
                  <div style={{ color: phone ? colors.pageText : colors.muted }}>{phone || "Telefon yok"}</div>
                  <div style={{ color: hasEmail ? colors.pageText : colors.muted }}>{contact.email || "E-posta yok"}</div>
                  <div style={{ color: phone || hasEmail ? "#166534" : "#b45309", fontWeight: 950 }}>
                    {channelLabel}
                  </div>
                  <button
                    type="button"
                    onClick={() => openWhatsAppForContact(contact)}
                    disabled={!phone}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "7px 9px",
                      background: phone ? "#dcfce7" : colors.soft,
                      color: phone ? "#166534" : colors.muted,
                      fontWeight: 950,
                      cursor: phone ? "pointer" : "not-allowed",
                      fontSize: 11
                    }}
                  >
                    WhatsApp
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
      )}

      {activeSection === "firms" && (
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: colors.pageText, fontSize: 18 }}>ecalpanel Carileri</h3>
            <div style={{ color: colors.muted, fontWeight: 750, marginTop: 4, fontSize: 12 }}>
              Sadece Paraşüt’te <strong>ecalpanel</strong> kategorisinde olan cariler, bakiye büyükten küçüğe sıralı
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
            {messageContext === "sync" && (busyMessage || resultMessage || errorMessage) && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: 36,
                  padding: "7px 10px",
                  borderRadius: 999,
                  background: busyMessage ? "#dbeafe" : errorMessage ? "#fee2e2" : "#dcfce7",
                  color: busyMessage ? "#1d4ed8" : errorMessage ? "#991b1b" : "#166534",
                  border: busyMessage ? "1px solid #93c5fd" : errorMessage ? "1px solid #fecaca" : "1px solid #86efac",
                  fontSize: 12,
                  fontWeight: 950,
                  whiteSpace: "nowrap"
                }}
              >
                {busyMessage || resultMessage || errorMessage}
              </span>
            )}
            <button
              type="button"
              onClick={syncContacts}
              disabled={Boolean(busyMessage) || !status?.connected}
              style={{ ...primaryButtonStyle, opacity: status?.connected ? 1 : 0.55 }}
            >
              Carileri Senkronize Et
            </button>
            <button type="button" onClick={loadContacts} disabled={loadingContacts} style={secondaryButtonStyle}>
              Listeyi Yenile
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(120px, 1fr))", gap: 8, marginTop: 12 }}>
          <StatCard label="ecalpanel cari" value={contactStats.total} colors={colors} isDark={isDark} compact />
          <StatCard label="Portal eşleşmesi" value={contactStats.matched} colors={colors} isDark={isDark} compact />
          <StatCard label="Vergi no var" value={contactStats.withTaxNumber} colors={colors} isDark={isDark} compact />
          <StatCard label="Bakiye alanı var" value={contactStats.withBalance} colors={colors} isDark={isDark} compact />
          <StatCard label="Bekleyen bakiye toplamı" value={formatMoney(filteredPendingBalanceTotal)} colors={colors} isDark={isDark} compact accent />
        </div>

        <div className={isDark ? "" : "force-light-form"} style={{ marginTop: 10 }}>
          <input
            value={contactSearchText}
            onChange={(event) => setContactSearchText(event.target.value)}
            placeholder="ecalpanel carilerinde cari adı, vergi no, e-posta veya portal firması ara..."
            style={{ ...inputStyle, padding: "9px 11px", fontSize: 12 }}
          />
        </div>

        <div style={{ marginTop: 10, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "44px minmax(260px, 2fr) minmax(88px, 0.55fr) minmax(118px, 0.8fr) minmax(130px, 0.85fr) minmax(82px, 0.5fr) minmax(120px, 0.7fr) minmax(96px, 0.55fr)",
              gap: 8,
              padding: "8px 10px",
              background: colors.soft,
              color: colors.muted,
              fontSize: 11,
              fontWeight: 950
            }}
          >
            <div style={{ textAlign: "center" }}>#</div>
            <div>Cari</div>
            <div>Vergi No</div>
            <div style={{ textAlign: "right", paddingRight: 8 }}>Bakiye</div>
            <div>İletişim</div>
            <div>Kategori</div>
            <div>Portal Eşleşmesi</div>
            <div>İşlem</div>
          </div>

          {loadingContacts ? (
            <div style={{ padding: 18, color: colors.muted, fontWeight: 850 }}>Cariler yükleniyor...</div>
          ) : filteredContacts.length === 0 ? (
            <div style={{ padding: 14, color: colors.muted, fontWeight: 850, fontSize: 12 }}>
              {contacts.length ? "Aramaya uygun cari bulunamadı." : "ecalpanel kategorisinde cari bulunamadı. Kategori yeni verildiyse önce senkronizasyon çalıştır."}
            </div>
          ) : (
            <div>
              {filteredContacts.map((contact, index) => (
                <div
                  key={contact.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "44px minmax(260px, 2fr) minmax(88px, 0.55fr) minmax(118px, 0.8fr) minmax(130px, 0.85fr) minmax(82px, 0.5fr) minmax(120px, 0.7fr) minmax(96px, 0.55fr)",
                    gap: 8,
                    padding: "7px 10px",
                    borderTop: `1px solid ${colors.border}`,
                    color: colors.pageText,
                    fontWeight: 800,
                    alignItems: "center",
                    fontSize: 12
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <span
                      style={{
                        minWidth: 26,
                        height: 24,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: isDark ? "#1e293b" : "#eef6f9",
                        color: isDark ? "#d7e3f1" : "#124254",
                        border: isDark ? "1px solid #334155" : "1px solid #dbeafe",
                        fontSize: 11,
                        fontWeight: 1000
                      }}
                    >
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 950, lineHeight: 1.25 }}>{contact.name || "-"}</div>
                    <div style={{ color: colors.muted, fontSize: 10, marginTop: 1 }}>{contact.id}</div>
                  </div>
                  <div>{contact.taxNumber || "-"}</div>
                  <div style={{ textAlign: "right" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        justifyContent: "flex-end",
                        minWidth: 96,
                        padding: "5px 8px",
                        borderRadius: 10,
                        fontWeight: 1000,
                        fontSize: 12,
                        letterSpacing: "-0.01em",
                        ...getBalanceBadgeStyle(contact.balance)
                      }}
                    >
                      {formatMoney(contact.balance)}
                    </span>
                  </div>
                  <div style={{ color: colors.muted, fontSize: 11, lineHeight: 1.3 }}>
                    <div>{contact.email || "-"}</div>
                    <div>{contact.phone || ""}</div>
                  </div>
                  <div>{contact.category || "-"}</div>
                  <div>
                    {contact.portalMatch ? (
                      <span style={{ display: "inline-flex", padding: "4px 7px", borderRadius: 999, background: "#dcfce7", color: "#166534", fontWeight: 950, fontSize: 11 }}>
                        {contact.portalMatch.shortName || contact.portalMatch.name}
                      </span>
                    ) : (
                      <span style={{ color: colors.muted }}>Eşleşmedi</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openWhatsAppForContact(contact)}
                    disabled={!normalizePhoneForWhatsApp(contact.phone)}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "6px 8px",
                      background: normalizePhoneForWhatsApp(contact.phone) ? "#dcfce7" : colors.soft,
                      color: normalizePhoneForWhatsApp(contact.phone) ? "#166534" : colors.muted,
                      fontWeight: 950,
                      cursor: normalizePhoneForWhatsApp(contact.phone) ? "pointer" : "not-allowed",
                      fontSize: 11
                    }}
                  >
                    WhatsApp
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {messageContext !== "sync" && (busyMessage || resultMessage || errorMessage) && (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {busyMessage && <MessageBox type="info" text={busyMessage} />}
          {resultMessage && <MessageBox type="success" text={resultMessage} />}
          {errorMessage && <MessageBox type="error" text={errorMessage} />}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, colors }) {
  return (
    <div style={{ display: "grid", gap: 3 }}>
      <div style={{ color: colors.muted, fontSize: 12, fontWeight: 900 }}>{label}</div>
      <div style={{ color: colors.pageText, fontWeight: 900, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

function StatCard({ label, value, colors, isDark, compact = false, accent = false }) {
  return (
    <div
      style={{
        padding: compact ? 10 : 14,
        borderRadius: compact ? 12 : 14,
        border: accent
          ? (isDark ? "1px solid #22c55e" : "1px solid #86efac")
          : `1px solid ${colors.border}`,
        background: accent
          ? (isDark ? "#123226" : "#f0fdf4")
          : (isDark ? "#101827" : "#f8fafc")
      }}
    >
      <div style={{ color: accent ? (isDark ? "#bbf7d0" : "#166534") : colors.muted, fontSize: compact ? 10 : 12, fontWeight: 950 }}>{label}</div>
      <div style={{ color: accent ? (isDark ? "#dcfce7" : "#14532d") : colors.pageText, fontSize: compact ? 18 : 22, fontWeight: 950, marginTop: compact ? 3 : 5 }}>{value}</div>
    </div>
  );
}

function MessageBox({ type, text }) {
  const styleByType = {
    info: { background: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
    success: { background: "#dcfce7", color: "#166534", border: "#86efac" },
    error: { background: "#fee2e2", color: "#991b1b", border: "#fecaca" }
  };
  const style = styleByType[type] || styleByType.info;

  return (
    <div style={{ padding: "11px 13px", borderRadius: 12, background: style.background, color: style.color, border: `1px solid ${style.border}`, fontWeight: 900 }}>
      {text}
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("tr-TR");
}

function normalizeMatchText(value) {
  return String(value || "")
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9ğüşıöç]+/gi, " ")
    .replace(/\b(anonim|limited|sirketi|sirketi|ticaret|as|a s|ltd|şti|sti|teknoloji|yazilim|bilisim)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findPortalMatch(contact, firmOptions) {
  const contactNameKey = normalizeMatchText(contact?.name);

  if (!contactNameKey) return null;

  return firmOptions.find((firma) => {
    if (!firma.nameKey) return false;

    return contactNameKey === firma.nameKey ||
      contactNameKey.includes(firma.nameKey) ||
      firma.nameKey.includes(contactNameKey) ||
      (firma.shortKey && contactNameKey.includes(firma.shortKey));
  }) || null;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) return String(value);

  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2
  }).format(numberValue);
}

function normalizePhoneForDisplay(value) {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.length === 10 && digits.startsWith("5")) {
    return `0${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("0")) {
    return digits;
  }

  if (digits.length === 12 && digits.startsWith("90")) {
    return `0${digits.slice(2)}`;
  }

  return "";
}

function normalizePhoneForWhatsApp(value) {
  const digits = String(value || "").replace(/\D/g, "");

  if (digits.length === 10 && digits.startsWith("5")) {
    return `90${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("05")) {
    return `9${digits}`;
  }

  if (digits.length === 12 && digits.startsWith("90")) {
    return digits;
  }

  return "";
}

function formatMoneyForMessage(value) {
  if (value === null || value === undefined || value === "") return "-";

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) return `${value} TL`;

  return `${new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numberValue)} TL`;
}

function renderClientTemplate(template, contact) {
  return String(template || "")
    .replace(/\{firma\}/g, contact?.name || "")
    .replace(/\{bakiye\}/g, formatMoneyForMessage(contact?.balance))
    .replace(/\{vergiNo\}/g, contact?.taxNumber || "")
    .replace(/\{kategori\}/g, contact?.category || "");
}

export default ParasutIntegrationView;
